import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, Session } from '@supabase/supabase-js'

interface AuthState {
  user: User | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  userProfile: UserProfile | null
  familyGroup: FamilyGroup | null
}

interface UserProfile {
  id: string
  clerk_id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  subscription_status: 'active' | 'inactive' | 'expired' | 'cancelled'
  subscription_type: 'individual' | 'family' | null
  subscription_ends_at: string | null
  family_group_id: string | null
  created_at: string
  updated_at: string
}

interface FamilyGroup {
  id: string
  primary_user_id: string
  group_name: string | null
  member_count: number
  max_members: number
  subscription_starts_at: string | null
  subscription_ends_at: string | null
  is_active: boolean
}

interface AuthActions {
  setUser: (user: User | null) => void
  setSession: (session: Session | null) => void
  setUserProfile: (profile: UserProfile | null) => void
  setFamilyGroup: (group: FamilyGroup | null) => void
  setLoading: (loading: boolean) => void
  signOut: () => void
  hasActiveSubscription: () => boolean
  canInviteMembers: () => boolean
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      session: null,
      isAuthenticated: false,
      isLoading: true,
      userProfile: null,
      familyGroup: null,

      // Actions
      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false
        })
      },

      setSession: (session) => {
        set({
          session,
          isAuthenticated: !!session?.user,
          user: session?.user || null
        })
      },

      setUserProfile: (userProfile) => {
        set({ userProfile })
      },

      setFamilyGroup: (familyGroup) => {
        set({ familyGroup })
      },

      setLoading: (isLoading) => {
        set({ isLoading })
      },

      signOut: () => {
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          userProfile: null,
          familyGroup: null,
          isLoading: false,
        })
      },

      hasActiveSubscription: () => {
        const { userProfile } = get()
        if (!userProfile) return false

        const isActive = userProfile.subscription_status === 'active'
        const hasValidDate = userProfile.subscription_ends_at
          ? new Date(userProfile.subscription_ends_at) > new Date()
          : false

        return isActive && hasValidDate
      },

      canInviteMembers: () => {
        const { familyGroup, userProfile } = get()
        if (!familyGroup || userProfile?.subscription_type !== 'family') return false

        return familyGroup.member_count < familyGroup.max_members
      },
    }),
    {
      name: 'cyrano-auth',
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        isAuthenticated: state.isAuthenticated,
        userProfile: state.userProfile,
        familyGroup: state.familyGroup,
      }),
    }
  )
)